/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpResponse, HttpContext, HttpParams } from '@angular/common/http';
import { DataBaseService } from '../data-base-service';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { UpsertPermission } from '../models/upsert-permission';
import { GetCurrentUserPermissions } from '../models/get-current-user-permissions';
import { GetPermissionsList } from '../models/get-permissions-list';
import { Count as LoopbackCount } from '../models/loopback/count';
import { PermissionList } from '../models/permission-list';

@Injectable({ providedIn: 'root' })
export class AccessControllerService extends DataBaseService {

  /**
   * Path part for operation accessControllerCanAccessObject
   */
  static readonly AccessControllerCanAccessObjectPath = '/v1/access-object/can-access/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `canAccessObject()` instead.
   *
   * This method doesn't expect any request body.
   */
  canAccessObject$Response(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerCanAccessObjectPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `canAccessObject$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  canAccessObject(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<void> {

    return this.canAccessObject$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation accessControllerFindAccessObjectById
   */
  static readonly AccessControllerFindAccessObjectByIdPath = '/v1/access-object/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findAccessObjectById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findAccessObjectById$Response(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerFindAccessObjectByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findAccessObjectById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findAccessObjectById(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<void> {

    return this.findAccessObjectById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation accessControllerGetUserPermissions
   */
  static readonly AccessControllerGetUserPermissionsPath = '/v1/permissions/current_user';

  /**
   * Get all permissions for the current user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUserPermissions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserPermissions$Response(params?: {
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<GetCurrentUserPermissions>>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerGetUserPermissionsPath, 'get');
    if (params) {
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<GetCurrentUserPermissions>>;
      })
    );
  }
  /**
   * Get all permissions for the current user
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getUserPermissions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserPermissions(params?: {
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<GetCurrentUserPermissions>> {

    return this.getUserPermissions$Response(params).pipe(
      map((r: StrictHttpResponse<Array<GetCurrentUserPermissions>>) => r.body as Array<GetCurrentUserPermissions>)
    );
  }

  /**
   * Path part for operation accessControllerSystemCount
   */
  static readonly AccessControllerSystemCountPath = '/v1/permissions/list/{entitledEntityType}/count';

  /**
   * Get permissions count
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `systemCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  systemCount$Response(params: {
    entitledEntityType: string;
    moduleId?: string;
    where?: any;
    include_virtual?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerSystemCountPath, 'get');
    if (params) {
      rb.path('entitledEntityType', params.entitledEntityType, {});
      rb.query('moduleId', params.moduleId, {});
      rb.query('where', params.where, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }
  /**
   * Get permissions count
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `systemCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  systemCount(params: {
    entitledEntityType: string;
    moduleId?: string;
    where?: any;
    include_virtual?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<LoopbackCount> {

    return this.systemCount$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation accessControllerGetSystemPermissions
   */
  static readonly AccessControllerGetSystemPermissionsPath = '/v1/permissions/list/{entitledEntityType}';

  /**
   * List all users/groups in the system with permissions
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSystemPermissions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSystemPermissions$Response(params: {
    entitledEntityType: string;
    moduleId?: string;
    filter?: any;
    include_virtual?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<PermissionList>>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerGetSystemPermissionsPath, 'get');
    if (params) {
      rb.path('entitledEntityType', params.entitledEntityType, {});
      rb.query('moduleId', params.moduleId, {});
      rb.query('filter', params.filter, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<PermissionList>>;
      })
    );
  }
  /**
   * List all users/groups in the system with permissions
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getSystemPermissions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSystemPermissions(params: {
    entitledEntityType: string;
    moduleId?: string;
    filter?: any;
    include_virtual?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<PermissionList>> {

    return this.getSystemPermissions$Response(params).pipe(
      map((r: StrictHttpResponse<Array<PermissionList>>) => r.body as Array<PermissionList>)
    );
  }

  /**
   * Path part for operation accessControllerGetPermissionsCount
   */
  static readonly AccessControllerGetPermissionsCountPath = '/v1/permissions/{entitlementType}/{entitledEntityType}/count';

  /**
   * Get permission count
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPermissionsCount()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPermissionsCount$Response(params: {
    entitlementType: string;
    entitledEntityType: string;
    entitlementId?: string;
    where?: any;
    include_virtual?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerGetPermissionsCountPath, 'get');
    if (params) {
      rb.path('entitlementType', params.entitlementType, {});
      rb.path('entitledEntityType', params.entitledEntityType, {});
      rb.query('entitlementId', params.entitlementId, {});
      rb.query('where', params.where, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }
  /**
   * Get permission count
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPermissionsCount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPermissionsCount(params: {
    entitlementType: string;
    entitledEntityType: string;
    entitlementId?: string;
    where?: any;
    include_virtual?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<LoopbackCount> {

    return this.getPermissionsCount$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation accessControllerUpdateAccessPermission
   */
  static readonly AccessControllerUpdateAccessPermissionPath = '/v1/permissions/{entitlementType}/{entitlementId}/{entitledEntityType}/{entitledEntityId}';

  /**
   * Update/revoke a access permissions that belongs to the user/group
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAccessPermission()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAccessPermission$Response(params: {
    entitlementType: string;
    entitlementId: string;
    entitledEntityType: string;
    entitledEntityId: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: UpsertPermission
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerUpdateAccessPermissionPath, 'put');
    if (params) {
      rb.path('entitlementType', params.entitlementType, {});
      rb.path('entitlementId', params.entitlementId, {});
      rb.path('entitledEntityType', params.entitledEntityType, {});
      rb.path('entitledEntityId', params.entitledEntityId, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Update/revoke a access permissions that belongs to the user/group
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateAccessPermission$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAccessPermission(params: {
    entitlementType: string;
    entitlementId: string;
    entitledEntityType: string;
    entitledEntityId: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: UpsertPermission
  }): Observable<any> {

    return this.updateAccessPermission$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation accessControllerGetPermissions
   */
  static readonly AccessControllerGetPermissionsPath = '/v1/permissions/{entitlementType}/{entitledEntityType}';

  /**
   * List users/groups with system, module, entity and view permissions
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPermissions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPermissions$Response(params: {
    entitlementType: string;
    entitledEntityType: string;
    entitlementId?: string;
    filter?: any;
    include_virtual?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<GetPermissionsList>>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerGetPermissionsPath, 'get');
    if (params) {
      rb.path('entitlementType', params.entitlementType, {});
      rb.path('entitledEntityType', params.entitledEntityType, {});
      rb.query('entitlementId', params.entitlementId, {});
      rb.query('filter', params.filter, {});
      rb.query('include_virtual', params.include_virtual, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<GetPermissionsList>>;
      })
    );
  }
  /**
   * List users/groups with system, module, entity and view permissions
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPermissions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPermissions(params: {
    entitlementType: string;
    entitledEntityType: string;
    entitlementId?: string;
    filter?: any;
    include_virtual?: boolean;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<GetPermissionsList>> {

    return this.getPermissions$Response(params).pipe(
      map((r: StrictHttpResponse<Array<GetPermissionsList>>) => r.body as Array<GetPermissionsList>)
    );
  }

  /**
   * Path part for operation accessControllerFindAccessObjectByIdV2
   */
  static readonly AccessControllerFindAccessObjectByIdV2Path = '/v2/access-object/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findAccessObjectByIdV2()` instead.
   *
   * This method doesn't expect any request body.
   */
  findAccessObjectByIdV2$Response(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, AccessControllerService.AccessControllerFindAccessObjectByIdV2Path, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findAccessObjectByIdV2$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findAccessObjectByIdV2(params: {
    id: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<void> {

    return this.findAccessObjectByIdV2$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
